import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

const UserModal = ({ user, onClose, onSave }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        pdfMonthlyQuota: 30,
        isAdmin: false,
        isDemo: false,
        isExpert: false
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (user) {
            setFormData({
                id: user.id,
                name: user.name || '',
                email: user.email || '',
                password: '',
                pdfMonthlyQuota: user.pdfMonthlyQuota || 30,
                isAdmin: user.isAdmin || false,
                isDemo: user.isDemo || false,
                isExpert: user.isExpert || false
            });
        }
    }, [user]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;

        setFormData(prev => ({ ...prev, [name]: newValue }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = 'Le nom est requis';
        
        if (!formData.email.trim()) {
            newErrors.email = 'L\'email est requis';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Format d\'email invalide';
        }

        // VALIDATION PASSWORD RETIRÉE POUR LA CRÉATION (puisque c'est par mail)
        // On garde la validation seulement si on est en édition et qu'on essaie de changer le mdp
        if (user && formData.password && formData.password.length < 6) {
            newErrors.password = 'Min. 6 caractères';
        }

        if (formData.pdfMonthlyQuota < 0) newErrors.pdfMonthlyQuota = 'Doit être positif';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validate()) onSave(formData);
    };

    return (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center">
            <div className="relative bg-white w-full max-w-md mx-auto rounded-lg shadow-lg p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">
                        {user ? 'Modifier l\'utilisateur' : 'Inviter un utilisateur'}
                    </h3>
                    <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-500">
                        <XMarkIcon className="h-6 w-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">Nom</label>
                        <input type="text" name="name" value={formData.name} onChange={handleChange} className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${errors.name ? 'border-red-500' : 'border-gray-300'}`} />
                        {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">Email</label>
                        <input type="email" name="email" value={formData.email} onChange={handleChange} className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${errors.email ? 'border-red-500' : 'border-gray-300'}`} />
                        {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                    </div>

                    {/* CHAMP MOT DE PASSE : Visible UNIQUEMENT en mode Modification (si user existe) */}
                    {user && (
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700">Nouveau mot de passe (optionnel)</label>
                            <input type="password" name="password" value={formData.password} onChange={handleChange} placeholder="Laisser vide pour ne pas changer" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm" />
                            {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
                        </div>
                    )}
                    
                    {!user && (
                         <div className="mb-4 p-3 bg-blue-50 text-blue-700 text-sm rounded-md">
                            ℹ️ Un email sera envoyé à l'utilisateur pour qu'il crée son mot de passe.
                         </div>
                    )}

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">Quota mensuel</label>
                        <input type="number" name="pdfMonthlyQuota" value={formData.pdfMonthlyQuota} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm" />
                    </div>

                    <div className="flex flex-wrap gap-4 items-center mb-6 px-1">
                        <div className="flex items-center">
                            <input id="isAdmin" name="isAdmin" type="checkbox" checked={formData.isAdmin} onChange={handleChange} className="h-4 w-4 text-[#3D5A80] border-gray-300 rounded" />
                            <label htmlFor="isAdmin" className="ml-2 block text-sm text-gray-900">Administrateur</label>
                        </div>
                        <div className="flex items-center">
                            <input id="isExpert" name="isExpert" type="checkbox" checked={formData.isExpert} onChange={handleChange} className="h-4 w-4 text-[#3D5A80] border-gray-300 rounded" />
                            <label htmlFor="isExpert" className="ml-2 block text-sm text-gray-900">Expert</label>
                        </div>
                        <div className="flex items-center">
                            <input id="isDemo" name="isDemo" type="checkbox" checked={formData.isDemo} onChange={handleChange} className="h-4 w-4 text-[#3D5A80] border-gray-300 rounded" />
                            <label htmlFor="isDemo" className="ml-2 block text-sm text-gray-900">Compte Démo</label>
                        </div>
                    </div>

                    <div className="flex justify-end space-x-3 pt-2 border-t border-gray-100">
                        <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">Annuler</button>
                        <button type="submit" className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-[#3D5A80] hover:bg-[#2b3f59]">
                            {user ? 'Mettre à jour' : 'Envoyer l\'invitation'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default UserModal;